import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Clock, ChevronDown, ChevronUp, HelpCircle } from "lucide-react";
import { useState } from "react";

// FAQ Accordion Item Component
const FAQItem = ({ question, answer, links = [] }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div 
      className="card mb-4 overflow-hidden"
      style={{ background: "var(--background-200)", borderColor: "rgba(176, 176, 176, 0.15)" }}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-6 text-left"
        style={{ color: "var(--text-100)" }}
      >
        <span className="font-bold text-xl sm:text-2xl pr-4" style={{ fontFamily: "'Smooch Sans', sans-serif" }}>
          {question}
        </span>
        {isOpen ? (
          <ChevronUp size={24} style={{ color: "var(--primary-300)", flexShrink: 0 }} />
        ) : (
          <ChevronDown size={24} style={{ color: "var(--primary-300)", flexShrink: 0 }} />
        )}
      </button>
      
      {isOpen && (
        <div className="px-6 pb-6">
          <div className="text-base sm:text-lg" style={{ color: "var(--text-200)", lineHeight: "1.7" }}>
            {answer}
          </div>
          {links.length > 0 && (
            <div className="mt-4 pt-4" style={{ borderTop: "1px solid rgba(176, 176, 176, 0.15)" }}>
              {links.map((link, index) => (
                <span key={index}>
                  <Link 
                    to={link.href} 
                    className="text-gradient hover:underline"
                    style={{ fontWeight: 500 }}
                  >
                    {link.text}
                  </Link>
                  {index < links.length - 1 && <span className="mx-2" style={{ color: "var(--text-300)" }}>•</span>}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useState(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="https://www.machinerymarketed.com/app-logo.png" 
            alt="MachineryMarketed Logo" 
            className="h-10 w-10"
          />
          <span className="font-bold text-xl tracking-tight" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
            MACHINERYMARKETED
          </span>
        </Link>
        <Link to="/" className="btn-outline text-sm">Back to Home</Link>
      </div>
    </nav>
  );
};

// FAQ Data
const faqData = [
  {
    question: "1. Who is Machinery Marketed! actually built for?",
    answer: "MachineryMarketed is built specifically for ag, construction and turf machinery dealerships — salespeople, managers, and owners who love to trade, value, and resell used machinery every day.",
    links: [{ text: "Learn more: About Us", href: "/about" }]
  },
  {
    question: "2. What problem does MachineryMarketed solve?",
    answer: "It removes the guesswork and wasted time around trade-ins, makes calculating and setting the valuations easy and quick, and our best feature - we make marketing your trade-ins happen quickly, professionally and by placing your gear in every corner of the market - we make it sell.",
    links: [{ text: "See how it works: How the App Works", href: "/how-it-works" }]
  },
  {
    question: "3. How does this help my sales team sell more machines?",
    answer: "Salespeople can appraise trades properly on the spot, managers approve values faster, and machines get marketed sooner — less friction, faster deals. This frees up capital to spin the wheel again and ultimately - your dealership makes more profit.",
    links: [{ text: "Read more: Sales Workflow", href: "/sales-workflow" }]
  },
  {
    question: "4. Is this just another CRM?",
    answer: "No. MachineryMarketed is not a CRM at all. It focuses on trade appraisal, valuation, and machine marketing. It works alongside whatever systems you already use.",
    links: [{ text: "Read more: App Integrations", href: "/integrations" }]
  },
  {
    question: "5. How do I know my dealership data is safe?",
    answer: "We're industry people too. Data security has been paramount in the development of this app. All data is stored securely using industry-standard encryption, strict access controls, and role-based permissions. Your data stays your data — always.",
    links: [{ text: "Details here: Security & Data Protection", href: "/security" }]
  },
  {
    question: "6. Who owns the data and photos we upload?",
    answer: "You do. Full stop. Machinery Marketed! never claims ownership of your machines, images, or dealership data. We don't sell or release anything you or your team input to the app that you haven't agreed to and that isn't done with aim of marketing the gear and making it sell.",
    links: [{ text: "See: Privacy Policy", href: "/privacy" }]
  },
  {
    question: "7. Can my competitors see my machines or data?",
    answer: "No. Only your dealership and approved users can access your internal data. Public listings are only published when you choose and viewing publicly is the only way your competitor might see what you've traded.",
    links: []
  },
  {
    question: "8. Does this replace how we currently value machines?",
    answer: "No — it strengthens it, streamlines it, and gets it to market faster. MachineryMarketed supports manager-approved valuations, consistency, and accountability across your team.",
    links: [{ text: "See: Valuation Process", href: "/valuation" }]
  },
  {
    question: "9. How long does it take to appraise a machine?",
    answer: "Most appraisals take 10–15 minutes, including photos. Some can take longer, especially where the sales person is engaged with your customer. That's usually less time than a phone call and far more accurate.",
    links: [{ text: "See: The Appraisal Process", href: "/appraisal-process" }]
  },
  {
    question: "10. What if a salesperson misses something on an appraisal?",
    answer: "The app uses structured appraisal fields and photo prompts to reduce mistakes and highlight missing information before submission.",
    links: []
  },
  {
    question: "11. Can managers review and approve values remotely?",
    answer: "Yes, absolutely. Any of your team members can access the app from the web, from Android or from iPhone. Managers can review appraisals, photos, and notes from anywhere on any device, calculate and approve values, or send back for clarification.",
    links: [{ text: "Learn more: Manager Dashboard", href: "/manager-dashboard" }]
  },
  {
    question: "12. What happens once a trade is approved and taken?",
    answer: "All the appraisal data, photos, and approved pricing are used to instantly create marketing listings — awesome customisation of the listing and no double handling. Once click and it's pushed to as many platforms and as many potential buyers as you like.",
    links: [{ text: "See: From Appraisal to Market", href: "/appraisal-to-market" }]
  },
  {
    question: "13. Where can we list machines from the app?",
    answer: "Machines can be listed on your website, your social media accounts, email campaigns, and a heaps of selected third-party platforms (some of which your most likely already using - just faster) — all from one place. We will also list your equipment on our socials, here on our webpage and we can automate an email out to our database of over 3000 genuine machinery users/buyers instantly. You're in control from the start - you select where the listing go and you can change this based on performance.",
    links: [{ text: "Details: Marketing Distribution", href: "/marketing-distribution" }]
  },
  {
    question: "14. Do we need professional photographers or marketers?",
    answer: "No. The app is designed so salespeople can capture everything needed with a phone — clean, consistent, and ready to market.",
    links: []
  },
  {
    question: "15. Will this change how our dealership operates day-to-day?",
    answer: "Only for the better. Most dealerships find it simplifies workflows, saves time and frees hostage cash - the last thing we want to do is increase your teams workload.",
    links: []
  },
  {
    question: "16. How hard is it to get the team up and running?",
    answer: "Most teams are using the app comfortable within a day or two. The interface is simple, practical, and built for people who work in yards, paddocks and on-site — not offices. As your team become more familiar with the app, we work with them to make sure you're all getting the best you can from what we've created.",
    links: [{ text: "See: Getting Started", href: "/getting-started" }]
  },
  {
    question: "17. What support do we get if we need help?",
    answer: "You get real support from people who understand machinery dealerships, not a call centre reading scripts and not an AI ChatBot that has no idea what you're issue may be.",
    links: [{ text: "Visit: Support & Contact", href: "/#contact" }]
  },
  {
    question: "18. Is this suitable for small or family-run dealerships?",
    answer: "Absolutely. MachineryMarketed scales from single-site dealerships right through to multi-national dealer groups.",
    links: []
  },
  {
    question: "19. What does it cost?",
    answer: "Pricing is straightforward, transparent, and based on dealership needs — no hidden fees or surprises. No limit to the amount of gear you can list. All we need to know is 1. How many sales people you have in your team and 2. How many rooftops (no. of branches) you have. Then we give you an annual subscription cost.",
    links: [{ text: "Pricing: Get a quotation for my business", href: "/#contact" }]
  },
  {
    question: "20. Why should we trust MachineryMarketed over generic software?",
    answer: "Because it's built by people who know the machinery game, for people who live it every day. No fluff. Just tools that work.",
    links: [{ text: "Read more: Why Dealers Choose Us", href: "/why-choose-us" }]
  }
];

// Main FAQ Page Component
const FAQPage = () => {
  return (
    <div style={{ background: "var(--background-100)", minHeight: "100vh" }}>
      <Navigation />
      
      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="py-16">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-12">
              <div 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
                style={{ background: "var(--primary-100)" }}
              >
                <HelpCircle size={20} style={{ color: "white" }} />
                <span className="text-sm font-semibold" style={{ color: "white" }}>Got Questions?</span>
              </div>
              <h1 
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 px-2"
                style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)", wordWrap: "break-word" }}
              >
                Frequently Asked <span className="text-gradient">Questions</span>
              </h1>
              <p className="text-lg max-w-2xl mx-auto" style={{ color: "var(--text-200)" }}>
                Everything you need to know about MachineryMarketed. Can't find what you're looking for? 
                <Link to="/#contact" className="text-gradient hover:underline ml-1">Reach out to us</Link>.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Accordion Section */}
        <section className="pb-16">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto">
              {faqData.map((faq, index) => (
                <FAQItem 
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  links={faq.links}
                />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16" style={{ background: "var(--background-200)" }}>
          <div className="container mx-auto px-4 lg:px-8 text-center">
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}
            >
              Still have questions?
            </h2>
            <p className="mb-8" style={{ color: "var(--text-200)" }}>
              Our team is here to help. Get in touch and we'll get back to you within 24 hours.
            </p>
            <Link to="/#contact" className="btn-primary inline-flex items-center gap-2">
              Contact Us
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-12" style={{ background: "var(--background-200)", borderTop: "1px solid rgba(176, 176, 176, 0.1)" }}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://www.machinerymarketed.com/app-logo.png" 
                alt="MachineryMarketed Logo" 
                className="h-10 w-10"
              />
              <span className="font-bold text-lg" style={{ fontFamily: "'Smooch Sans', sans-serif", color: "var(--text-100)" }}>
                MACHINERYMARKETED
              </span>
            </div>

            <div className="flex items-center gap-6">
              <Link to="/privacy" className="footer-link text-sm">Privacy</Link>
              <Link to="/terms" className="footer-link text-sm">Terms</Link>
              <Link to="/cookies" className="footer-link text-sm">Cookies</Link>
              <Link to="/faq" className="footer-link text-sm">FAQ</Link>
            </div>

            <p className="text-sm" style={{ color: "var(--text-300)" }}>
              © 2026 MachineryMarketed. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FAQPage;
